import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isNativeApp } from "@/lib/native/platform";
import {
  flushTelemetry,
  sendSessionPing,
  setTelemetryUser,
  startTelemetrySession,
  trackEvent,
  trackScreen,
} from "@/lib/native/telemetry";

/**
 * Crash reporting + analytics bootstrap (native shell only).
 *
 * Starts a telemetry session on launch, tracks route changes as screen views,
 * attaches the signed-in admin to crash reports, and flushes queued events when
 * the app is backgrounded. Renders nothing and does nothing on the web.
 */
export const NativeTelemetryBoot = () => {
  const location = useLocation();
  const ready = useRef(false);

  useEffect(() => {
    if (!isNativeApp()) return;
    let cleanup: (() => void) | undefined;
    let removeState: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      cleanup = await startTelemetrySession();
      if (cancelled) {
        cleanup?.();
        return;
      }
      ready.current = true;

      // Attach the current user (and keep it in sync on sign in / sign out).
      const { data } = await supabase.auth.getUser();
      await setTelemetryUser(data.user?.id ?? null);

      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) {
            trackEvent("app_foreground");
            void sendSessionPing();
          } else {
            trackEvent("app_background");
            void flushTelemetry();
          }
        });
        if (cancelled) void handle.remove();
        else removeState = () => void handle.remove();
      } catch {
        /* plugin unavailable */
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void setTelemetryUser(session?.user?.id ?? null);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      removeState?.();
      cleanup?.();
    };
  }, []);

  // Screen views — route path only, never query strings (they can carry tokens).
  useEffect(() => {
    if (!isNativeApp() || !ready.current) return;
    trackScreen(location.pathname);
  }, [location.pathname]);

  return null;
};

export default NativeTelemetryBoot;
